/**
 * NotificationJourneyPage — Push & In-App notification tracking (First Chapter Journey).
 * Backed by GET /api/admin/notifications/journey-* on admin-backend (3001).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Button, Paper, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

import apiService from '../../services/api';
import TimeRangeFilter from '../common/TimeRangeFilter';
import useTimeRangeParam from '../common/useTimeRangeParam';
import { TimeRangeValue, resolveRangeYmd, toApiToken } from '../common/timeRange';
import SectionHeader from './SectionHeader';
import {
  FONT, FONT_H, FONT_MONO, SURFACE, MILESTONE_COLORS, convPctColor,
} from './funnelBands';
import {
  JourneySummaryData,
  SendsData,
  OfferPerformanceData,
  TimelineData,
  JOURNEY_STATE_LABELS,
  NOTIFICATION_KEY_HINTS,
} from './notificationJourneyTypes';

const PAGE_MAX_WIDTH = 1600;

const STATE_ORDER = [
  'installed', 'chapter_started', 'chapter_completed', 'shared',
  'offer_window', 'converted', 'exited',
];

/** The notifications routes take `range` plus `start`/`end` for a custom span. */
function buildQuery(range: TimeRangeValue): string {
  const params = new URLSearchParams({ range: toApiToken(range) });
  if (range.type === 'custom') {
    const { from, to } = resolveRangeYmd(range);
    params.set('start', from);
    params.set('end', to);
  }
  return params.toString();
}

function fmtPct(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `${n.toFixed(1)}%`;
}

const StatCard: React.FC<{
  label: string;
  value: string | number;
  sub?: string;
  color?: string;
  loading?: boolean;
}> = ({ label, value, sub, color = '#1A5F7A', loading }) => (
  <Paper elevation={0} sx={{
    bgcolor: SURFACE.paper, border: `1px solid ${SURFACE.line}`,
    borderTop: `4px solid ${color}`, borderRadius: '13px', p: '14px 16px', height: '100%',
  }}>
    {loading ? (
      <Skeleton variant="text" width="60%" />
    ) : (
      <>
        <Typography sx={{ fontFamily: FONT_MONO, fontSize: '9.5px', letterSpacing: '0.04em', textTransform: 'uppercase', color: SURFACE.slate }}>
          {label}
        </Typography>
        <Typography sx={{ fontFamily: FONT_MONO, fontSize: '28px', fontWeight: 800, lineHeight: 1, mt: 1, color }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </Typography>
        {sub && (
          <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: SURFACE.slate, mt: 0.5 }}>
            {sub}
          </Typography>
        )}
      </>
    )}
  </Paper>
);

const NotificationJourneyPage: React.FC = () => {
  const [range, setRange] = useTimeRangeParam({ type: '30d' });
  const [summary, setSummary] = useState<JourneySummaryData | null>(null);
  const [sends, setSends] = useState<SendsData | null>(null);
  const [offers, setOffers] = useState<OfferPerformanceData | null>(null);
  const [timeline, setTimeline] = useState<TimelineData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Monotonic request id (same discipline as MmpAnalyticsPage): every load
  // bumps it, and any await that resumes to find itself stale writes NOTHING.
  // apiService.get cannot carry an abort signal, so a superseded GET still
  // completes server-side — what matters is that it can no longer overwrite a
  // newer request's result.
  const reqSeq = useRef(0);
  const hasDataRef = useRef(false);

  const load = useCallback(async (isRefresh = false) => {
    const seq = ++reqSeq.current;
    const stale = () => reqSeq.current !== seq;
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    const q = buildQuery(range);
    try {
      const api = apiService as { get: (url: string) => Promise<{ data: unknown; cached?: boolean }> };
      const [sumRes, sendsRes, offersRes, timelineRes] = await Promise.all([
        api.get(`/api/admin/notifications/journey-summary?${q}`),
        api.get(`/api/admin/notifications/sends?${q}`),
        api.get(`/api/admin/notifications/offer-performance?${q}`),
        api.get(`/api/admin/notifications/timeline?${q}`),
      ]);
      if (stale()) return;
      hasDataRef.current = true;
      setSummary(sumRes.data as JourneySummaryData);
      setSends(sendsRes.data as SendsData);
      setOffers(offersRes.data as OfferPerformanceData);
      setTimeline(timelineRes.data as TimelineData);
    } catch (err: unknown) {
      if (stale()) return;
      const msg = err instanceof Error ? err.message : String(err);
      // A failed fetch leaves NO data behind — otherwise the previous range's
      // numbers sit under an error banner as if they described this one.
      // Clearing hasDataRef also makes the next attempt a full (skeleton) load
      // rather than a soft refresh over an empty page.
      hasDataRef.current = false;
      setSummary(null);
      setSends(null);
      setOffers(null);
      setTimeline(null);
      setError(`Couldn't load notification journey data: ${msg}`);
    } finally {
      if (!stale()) {
        if (isRefresh) setRefreshing(false);
        else setLoading(false);
      }
    }
  }, [range]);

  // The half-filled-custom guard this page used to need is gone with the date
  // TextFields: the shared filter only applies a custom span once both dates
  // are valid and ordered, so a partial range can no longer reach the wire.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load(hasDataRef.current);
  }, [range]);

  const failed = error != null;
  const awaiting = summary?.awaiting_migration || sends?.awaiting_migration;
  // "Journey hasn't started sending yet" is a PRODUCT claim. It may only be
  // made when a request actually succeeded — a 500 is not an empty period.
  const isEmpty = !loading && !failed && !awaiting
    && summary != null
    && (summary?.total_journeys ?? 0) === 0
    && (sends?.totals?.push_sent ?? 0) === 0;

  const visibleSendRows = (sends?.steps ?? []).filter(
    (row) => row.sent > 0 || row.key.startsWith('PN-') || row.key.startsWith('IA-'),
  );

  return (
    <Box sx={{ px: 2, py: 2.5 }}>
      <Box sx={{ maxWidth: PAGE_MAX_WIDTH, mx: 'auto', width: '100%' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography sx={{ fontFamily: FONT, fontSize: '11px', color: '#9CA3AF', mb: 0.25 }}>
              Analytics &amp; Insights → Push &amp; In-App
            </Typography>
            <Typography variant="h4" sx={{ fontFamily: FONT_H, fontWeight: 700, color: '#1A5F7A' }}>
              Push &amp; In-App Journey
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: '#5D6D7E', mt: 0.75, maxWidth: 760, lineHeight: 1.5 }}>
              First Chapter Journey — push notifications and in-app cards from install through VOICE50 offer.
              Per-step send/open rates, offer redemption, and daily volume.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <TimeRangeFilter
              value={range}
              onChange={setRange}
              onRefresh={() => load(true)}
              refreshing={refreshing}
            />
            {summary?.range && (
              <Typography sx={{ fontFamily: FONT, fontSize: '10px', color: '#9CA3AF' }}>
                {summary.range.from} → {summary.range.to}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Failure banner ≠ empty-period copy. These are two different facts and
            they get two different panels. */}
        {failed && (
          <Paper elevation={0} sx={{ border: '1px solid #FCA5A5', borderRadius: '12px', p: 3, mb: 3, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: FONT_H, fontSize: '16px', color: '#B0202F', mb: 0.75 }}>
              Couldn&apos;t load this page
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: '#B0202F', mb: 1.5 }}>
              {error} — the figures below are unknown, not zero.
            </Typography>
            <Button variant="outlined" color="error" size="small" onClick={() => load(hasDataRef.current)}>Retry</Button>
          </Paper>
        )}

        {!failed && isEmpty && (
          <Paper elevation={0} sx={{ border: `1px dashed ${SURFACE.line}`, borderRadius: '12px', p: 4, mb: 3, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: FONT_H, fontSize: '18px', color: '#1A5F7A', mb: 1 }}>
              Journey hasn&apos;t started sending yet
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: SURFACE.slate, maxWidth: 520, mx: 'auto' }}>
              Enrollments and sends appear here once <code>FIRST_CHAPTER_JOURNEY_ENABLED=true</code> and members enter the First Chapter Journey.
            </Typography>
          </Paper>
        )}

        {!failed && (
          <>
            {/* Section 1 — Journey overview */}
            <SectionHeader
              num={1}
              title="Journey Overview"
              description="How many members entered the journey, where they are now, and headline conversion / offer rates."
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
              <StatCard label="Total journeys" value={summary?.total_journeys ?? 0} loading={loading} />
              <StatCard
                label="Converted"
                value={summary?.by_state?.converted ?? 0}
                sub={fmtPct(summary?.conversion_rate) + ' conversion rate'}
                color={MILESTONE_COLORS.subscriber}
                loading={loading}
              />
              <StatCard
                label="In offer window"
                value={summary?.by_state?.offer_window ?? summary?.offer_window_count ?? 0}
                color="#7D1935"
                loading={loading}
              />
              <StatCard
                label="VOICE50 redeem rate"
                value={fmtPct(summary?.offer_redemption_rate)}
                sub="Shown → redeemed"
                color="#146152"
                loading={loading}
              />
            </Box>

            <Paper elevation={0} sx={{ border: `1px solid ${SURFACE.line}`, borderRadius: '12px', p: 2, mb: 2 }}>
              <Typography sx={{ fontFamily: FONT_MONO, fontSize: '10px', textTransform: 'uppercase', color: SURFACE.slate, mb: 1.5 }}>
                Members by journey state
              </Typography>
              {loading ? (
                <Skeleton variant="rounded" height={80} />
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {STATE_ORDER.map((state) => {
                    const count = summary?.by_state?.[state] ?? 0;
                    return (
                      <Box key={state} sx={{
                        px: 1.25, py: 0.75, borderRadius: '8px',
                        bgcolor: count > 0 ? '#e6f0ec' : '#F3F4F6',
                        border: '1px solid #E5E7EB',
                      }}>
                        <Typography sx={{ fontFamily: FONT_MONO, fontSize: '9px', color: SURFACE.slate }}>
                          {JOURNEY_STATE_LABELS[state] ?? state}
                        </Typography>
                        <Typography sx={{ fontFamily: FONT_MONO, fontSize: '16px', fontWeight: 700, color: '#1A5F7A' }}>
                          {count.toLocaleString()}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Paper>

            {/* Section 2 — Send performance */}
            <SectionHeader
              num={2}
              title="Send Performance"
              description="Each notification key (PN-01…PN-05B, IA-01…IA-05) — sent, opened, and open rate by channel."
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5, mb: 1.5 }}>
              <StatCard label="Push sent" value={sends?.totals?.push_sent ?? 0} loading={loading} />
              <StatCard label="In-app shown" value={sends?.totals?.in_app_shown ?? 0} loading={loading} />
              <StatCard label="Total opened" value={sends?.totals?.total_opened ?? 0} loading={loading} />
            </Box>
            <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${SURFACE.line}`, borderRadius: '12px', mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F9FAFB' }}>
                    {['Notification', 'Channel', 'Sent', 'Opened', 'Open rate'].map((h) => (
                      <TableCell key={h} sx={{ fontFamily: FONT_MONO, fontSize: '10px', fontWeight: 700, color: SURFACE.slate }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 5 }).map((__, j) => (
                          <TableCell key={j}><Skeleton width="80%" /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : visibleSendRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ fontFamily: FONT, fontSize: '13px', color: SURFACE.slate, py: 3, textAlign: 'center' }}>
                        No sends recorded for this period.
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleSendRows.map((row) => (
                      <TableRow key={`${row.key}-${row.channel}`} hover>
                        <TableCell sx={{ maxWidth: 420 }}>
                          <Typography sx={{ fontFamily: FONT_MONO, fontSize: '12px', fontWeight: 700, color: '#1A5F7A' }}>
                            {row.key}
                          </Typography>
                          <Typography sx={{ fontFamily: FONT, fontSize: '11px', color: SURFACE.slate, lineHeight: 1.35, mt: 0.25 }}>
                            {NOTIFICATION_KEY_HINTS[row.key] ?? row.key}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontFamily: FONT, fontSize: '12px', textTransform: 'capitalize' }}>{row.channel.replace('_', ' ')}</TableCell>
                        <TableCell sx={{ fontFamily: FONT_MONO, fontSize: '12px' }}>{row.sent.toLocaleString()}</TableCell>
                        <TableCell sx={{ fontFamily: FONT_MONO, fontSize: '12px' }}>{row.opened.toLocaleString()}</TableCell>
                        <TableCell sx={{
                          fontFamily: FONT_MONO, fontSize: '12px', fontWeight: 600,
                          color: convPctColor(row.open_rate),
                        }}>
                          {row.sent > 0 ? fmtPct(row.open_rate) : '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Section 3 — Offer performance */}
            <SectionHeader
              num={3}
              title="Offer Performance"
              description="VOICE50 (journey offer), CYNDY50 (campaign), and full-price conversions after PN-04 subscribe nudge."
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5, mb: 2 }}>
              <StatCard
                label="VOICE50 shown"
                value={offers?.voice50?.shown ?? 0}
                sub={`${offers?.voice50?.redeemed ?? 0} redeemed · ${fmtPct(offers?.voice50?.redemption_rate)}`}
                color="#1A5F7A"
                loading={loading}
              />
              <StatCard
                label="CYNDY50 issued"
                value={offers?.cyndy50?.issued ?? 0}
                sub={`${offers?.cyndy50?.redeemed ?? 0} redeemed · ${fmtPct(offers?.cyndy50?.redemption_rate)}`}
                color="#7D1935"
                loading={loading}
              />
              <StatCard
                label="Full price @ PN-04"
                value={offers?.full_price_conversions_at_pn04 ?? 0}
                sub="Subscribed without prior offer shown"
                color="#146152"
                loading={loading}
              />
            </Box>

            {/* Section 4 — Daily timeline */}
            <SectionHeader
              num={4}
              title="Daily Timeline"
              description="Push sends, in-app impressions, opens, and subscription conversions by day."
            />
            <Paper elevation={0} sx={{ border: `1px solid ${SURFACE.line}`, borderRadius: '12px', p: 2, mb: 4 }}>
              {loading ? (
                <Skeleton variant="rounded" height={280} />
              ) : (timeline?.series?.length ?? 0) === 0 ? (
                <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: SURFACE.slate, py: 4, textAlign: 'center' }}>
                  No timeline data for this period.
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={timeline?.series ?? []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontFamily: FONT_MONO, fontSize: 10 }}
                      tickFormatter={(v: string) => v.slice(5)}
                    />
                    <YAxis tick={{ fontFamily: FONT_MONO, fontSize: 10 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ fontFamily: FONT, fontSize: '12px' }}
                      labelFormatter={(l) => `Date: ${l}`}
                    />
                    <Legend wrapperStyle={{ fontFamily: FONT, fontSize: '11px' }} />
                    <Bar dataKey="push_sent" name="Push sent" fill="#1A5F7A" stackId="a" />
                    <Bar dataKey="in_app_shown" name="In-app shown" fill="#2A7F9A" stackId="a" />
                    <Bar dataKey="opened" name="Opened" fill="#D4AC0D" />
                    <Bar dataKey="conversions" name="Conversions" fill="#146152" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </>
        )}
      </Box>
    </Box>
  );
};

export default NotificationJourneyPage;
